/**
 * The family's optional mailing address (train 17; server: app/routes/mailing_address.py).
 *
 * Account holder only: a co-parent or a child gets 403 account_holder_required,
 * which every caller here treats as "not yours to see" and shows nothing.
 * The address comes back only from GET; writes answer `{asked, has_address}`.
 *
 * The ask is shown once per family, after a purchase (operator ruling,
 * 2026-10-23): CheckoutPanel's card success and a user-started Apple purchase
 * the server accepted call `requestMailingPrompt()`. The flag lives in
 * sessionStorage because a web subscribe hard-navigates to /subscribed, which
 * asks; `asked_at` on the server is what makes it once.
 */
import { request } from "../api";

export interface MailingAddress {
  name: string;
  line1: string;
  line2: string | null;
  city: string;
  region: string | null;
  postal_code: string;
  country: string;
}

export interface MailingAddressState {
  asked: boolean;
  has_address: boolean;
  address?: MailingAddress | null;
}

const BASE = "/family/mailing-address";

export function getMailingAddress(): Promise<MailingAddressState> {
  return request<MailingAddressState>(BASE);
}

export function saveMailingAddress(address: MailingAddress): Promise<MailingAddressState> {
  return request<MailingAddressState>(BASE, { method: "PUT", body: JSON.stringify(address) });
}

export function skipMailingAddress(): Promise<MailingAddressState> {
  return request<MailingAddressState>(`${BASE}/skip`, { method: "POST" });
}

export function removeMailingAddress(): Promise<MailingAddressState> {
  return request<MailingAddressState>(BASE, { method: "DELETE" });
}

export const MAILING_PROMPT_KEY = "wbm.mailingAddressPrompt";
export const MAILING_PROMPT_EVENT = "wbm:mailing-address-prompt";

/** A purchase went through and this page is about to leave: the next page asks. An event
 *  here would open the sheet for the hop to close, with the flag already spent (2026-10-31). */
export function noteMailingPrompt(): void {
  try {
    sessionStorage.setItem(MAILING_PROMPT_KEY, "1");
  } catch { /* storage blocked: Settings can still add it */ }
}

/** A purchase just went through and this page stays: ask now. */
export function requestMailingPrompt(): void {
  noteMailingPrompt();
  try {
    window.dispatchEvent(new Event(MAILING_PROMPT_EVENT));
  } catch { /* no window (tests) */ }
}

/** Read and clear the flag. */
export function takeMailingPrompt(): boolean {
  try {
    const on = sessionStorage.getItem(MAILING_PROMPT_KEY) === "1";
    sessionStorage.removeItem(MAILING_PROMPT_KEY);
    return on;
  } catch {
    return false;
  }
}
